/* HeroV3.jsx — port de hero-v3.jsx (handoff "Hero A - dev 2")
 * Opción A · Apilada compacta (única opción final).
 * Calculadora → botón "Continuar" → modal "¿Cómo quieres continuar?"
 *   · WhatsApp (recomendado): arma el mensaje con los datos y abre el chat.
 *   · Cotizar aquí: cotizador en línea de 4 pasos (Auto → Plan → Seguro → Solicitud).
 *
 * Diferencias vs handoff (a propósito):
 *   - NO renderiza TopBar (el header de la página viene de otro handoff).
 *   - NO renderiza el help-fab (el FAB es global de la página).
 *   - WA_PHONE/waLink vienen de config (número real), modales extraídos a
 *     components/modals/.
 */
import React, { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../../Context/AuthContext';
import { sourceTrimRoute, sourceTrimAccessEnabled } from '../../../../env';
import {
  fmtMXN, calcMonthly, useCountUp, Ico,
  MARCAS, YEARS, modelosOf, versionsOf,
} from '../../core/heroCore';
import { fireQuoteConversion } from '../../core/conversions';
import {
  Dropdown, PriceField, LeftCopy, ModeToggle, AICard, Sugs, PromptStrip, Thought,
  useHeroState,
} from './HeroShared';
import ContinueModal, { openWhatsApp, WAIcon, plantillaParams } from '../modals/ContinueModal';
import Cotizador from '../modals/CotizadorModal';
import LeadModal from '../modals/LeadModal';
import SeguroManualPanel from './SeguroManualPanel';
import { useHeroQuote } from '../../core/useHeroQuote';
import { labelPlaca } from '../../../../Utils/statePlateAddress';
import {
  ajustarPagoInicial, montoDesdePct, pctPagoInicial, parseMonto, mensajeAjuste,
  ENGANCHE_MIN_PCT_DEFAULT, ENGANCHE_MAX_PCT_DEFAULT,
} from '../../../../Utils/pagoInicial';
import rightLines from '../../assets/decor/right-lines.svg';
import leftLine from '../../assets/decor/left-line.svg';

/* ─── Reusable field fragments ────────────────────────────────────────── */
/* Modo manual (F2) y modo IA con auto resuelto contra RODAC (F4): opciones y
 * renta reales vía h.catalog/h.real/h.quote. IA sin resolver: mock de heroCore. */
function isReal(h) { return (h.mode === 'manual' || h.aiReal) && h.catalog; }
const titleCase = (s) => (s || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
function fieldProps(h) {
  return { modelos: modelosOf(h.state.marca), versions: versionsOf(h.state.marca, h.state.modelo) };
}
function MarcaDD(h, c) {
  if (isReal(h)) return <Dropdown value={h.state.marca} options={h.catalog.brands.map(b => b.brand)} placeholder={h.catalog.loadingField === 'marca' ? 'Cargando…' : 'Marca'} disabled={!h.state.year || h.catalog.loadingField === 'marca'} onChange={h.real.onMarca} compact={c} />;
  return <Dropdown value={h.state.marca} options={MARCAS} placeholder="Marca" onChange={h.onMarca} compact={c} />;
}
function ModeloDD(h, c) {
  if (isReal(h)) return <Dropdown value={h.state.modelo} options={h.catalog.models.map(m => m.type)} placeholder={h.catalog.loadingField === 'modelo' ? 'Cargando…' : 'Modelo'} disabled={!h.state.marca || h.catalog.loadingField === 'modelo'} onChange={h.real.onModelo} compact={c} />;
  const { modelos } = fieldProps(h);
  return <Dropdown value={h.state.modelo} options={modelos} placeholder="Modelo" disabled={!h.state.marca} onChange={h.onModelo} compact={c} />;
}
function YearDD(h, c) {
  if (isReal(h)) return <Dropdown value={h.state.year} options={h.catalog.years.map(y => y.year)} placeholder={h.catalog.loadingField === 'year' ? 'Cargando…' : 'Año'} disabled={h.catalog.loadingField === 'year'} onChange={h.real.onYear} compact={c} />;
  return <Dropdown value={h.state.year} options={YEARS} placeholder="Año" onChange={(y) => h.setField({ year: y })} compact={c} />;
}
function VersionDD(h, c) {
  if (isReal(h)) return <Dropdown value={h.state.version} options={h.catalog.versions.map(v => v.description)} placeholder={h.catalog.loadingField === 'version' ? 'Cargando…' : 'Versión'} disabled={!h.state.modelo || h.catalog.loadingField === 'version'} onChange={h.real.onVersion} compact={c} />;
  const { versions } = fieldProps(h);
  return <Dropdown value={h.state.version} options={versions.map(([v, p]) => ({ value: v, label: v, extra: fmtMXN(p) }))} placeholder="Versión" disabled={!h.state.modelo} onChange={h.onVersion} compact={c} />;
}
function CarroceriaDD(h, c) { return <Dropdown value={h.state.carroceria} options={[{ value: 'sedan', label: 'Sedan' }, { value: 'suv', label: 'SUV' }, { value: 'pickup', label: 'Pickup' }]} placeholder="Tipo" onChange={(x) => h.setField({ carroceria: x })} compact={c} />; }
function MotorDD(h, c) { return <Dropdown value={h.state.motor} options={[{ value: 'gasolina', label: 'Gasolina' }, { value: 'híbrido', label: 'Híbrido' }]} placeholder="Motor" onChange={(m) => h.setField({ motor: m })} compact={c} />; }
/* Placas (P3): su dirección cotiza seguro y renta. Sin catálogo (getStaticData
 * no ha llegado o falló) queda fija en CDMX, que es con lo que se cotiza. */
function PlacaDD(h, c) {
  const options = (h.placas || []).map(p => ({ value: p.id, label: labelPlaca(p) }));
  return <Dropdown value={h.state.placa} options={options} placeholder="CDMX" disabled={!options.length} onChange={(id) => h.setField({ placa: id })} compact={c} />;
}
/* Seguro manual (P4): solo admin Pei, cuando RODAC no cotizó el auto ('auto',
 * ya sin loading) o si ya está aplicado (para editarlo o quitarlo). Con 'plazo' no. */
function showSeguroManual(h) {
  if (!isReal(h) || !h.esAdmin) return false;
  return (h.quote.noInsurance === 'auto' && !h.quote.loading) || !!h.seguroManual?.aplicado;
}
function PlanSeg(h) {
  return (
    <div className={`segp${h.msi?.show ? ' has-msi' : ''}`}>
      <button data-k="financiero" className={h.state.plan === 'financiero' ? 'on' : ''} onClick={() => h.setField({ plan: 'financiero' })}>Financiero</button>
      <button data-k="puro" className={h.state.plan === 'puro' ? 'on' : ''} onClick={() => h.setField({ plan: 'puro' })}>Puro · fiscal</button>
      {/* MSI: atajo solo para usuarios con sesión (type_user 1/3/4), igual que la
          landing vieja — no cotiza aquí, navega al flujo source-trim. */}
      {h.msi?.show && (
        <button data-k="msi" title="Meses sin intereses" onClick={h.msi.go}>MSI</button>
      )}
    </div>
  );
}
function PlazoChips(h) {
  return (
    <div className="pchip">
      {[24, 36, 48, 60].map(p => (<button key={p} className={h.state.plazo === p ? 'on' : ''} onClick={() => h.setField({ plazo: p })}>{p}</button>))}
    </div>
  );
}
/* ── Pago inicial (docs/plans/calculadoras-pago-inicial-libre.md) ───────────
 * Límites en % de getStaticData (enganche_minimo/maximo, vía h.downPayment) con
 * fallback 10/40 mientras carga. La barra se mueve en % entero; el monto puede
 * escribirse exacto (link "Escribir monto") y el % se deriva. Fuera de rango → se ajusta al límite. */
function limitesDe(h) {
  const dp = h.downPayment || {};
  const minPct = Number.isFinite(+dp.minPct) && +dp.minPct > 0 ? +dp.minPct : ENGANCHE_MIN_PCT_DEFAULT;
  const maxPct = Number.isFinite(+dp.maxPct) && +dp.maxPct > minPct ? +dp.maxPct : Math.max(minPct, ENGANCHE_MAX_PCT_DEFAULT);
  return { minPct, maxPct };
}

function EngancheSlider({ h, minPct, maxPct }) {
  const { precio, enganche } = h.state;
  const engPct = precio ? Math.round((enganche / precio) * 100) : 0;
  // commit-on-release: mientras arrastras solo se actualiza el % visual (drag);
  // el setField (que dispara la cotización) ocurre SOLO al soltar → 1 sola
  // petición en vez de una por cada paso intermedio del slider.
  const [drag, setDrag] = useState(null);
  const base = engPct ? Math.min(maxPct, Math.max(minPct, engPct)) : minPct;
  const shown = drag != null ? drag : base;
  const span = maxPct - minPct || 1;
  const commit = () => {
    if (drag == null) return;
    // al peso (sin redondeo a miles) y nunca fuera de los límites
    h.setField({ enganche: montoDesdePct({ pct: drag, base: precio, minPct, maxPct }) });
    setDrag(null);
  };
  const ticks = [0, 1, 2, 3].map(i => Math.round(minPct + (span * i) / 3));
  return (
    <>
      <input type="range" min={minPct} max={maxPct} step="1" value={shown}
        aria-label="Pago inicial (%)"
        onChange={(e) => setDrag(parseInt(e.target.value, 10))}
        onPointerUp={commit} onTouchEnd={commit} onKeyUp={commit} onBlur={commit}
        style={{ ['--pct']: ((shown - minPct) / span * 100) + '%' }} disabled={!precio} />
      <div className="ticks">{ticks.map((t, i) => <span key={i}>{t}%</span>)}</div>
    </>
  );
}

/* Input del monto exacto: foco y texto seleccionado al abrir; Enter o blur
 * aplica, Esc cancela (el blur que llega al desmontar ya no aplica). */
function EngancheInput({ inicial, precio, onApply, onCancel }) {
  const [draft, setDraft] = useState(inicial ? String(inicial) : '');
  const ref = useRef(null);
  const doneRef = useRef(false);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.focus();
    ref.current.select();
  }, []);
  const finish = (apply) => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (apply) onApply(draft); else onCancel();
  };
  const n = parseMonto(draft);
  return (
    <div className="eng-edit-row">
      <div className="price-fld focused">
        <span className="cur">$</span>
        <input ref={ref} type="text" inputMode="decimal" value={draft}
          aria-label="Monto exacto del pago inicial"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); finish(true); }
            else if (e.key === 'Escape') { e.preventDefault(); finish(false); }
          }}
          onBlur={() => finish(true)} />
      </div>
      {n != null && precio > 0 && <span className="eng-pct">≈ {pctPagoInicial(n, precio)}%</span>}
    </div>
  );
}

function EngancheField({ h }) {
  const { precio, enganche } = h.state;
  const { minPct, maxPct } = limitesDe(h);
  const [editing, setEditing] = useState(false);
  const [aviso, setAviso] = useState(null); // { texto } — objeto nuevo reinicia el timer
  useEffect(() => {
    if (!aviso) return undefined;
    const t = setTimeout(() => setAviso(null), 4000);
    return () => clearTimeout(t);
  }, [aviso]);

  const puedeEditar = precio > 0;
  const abrir = () => { setAviso(null); setEditing(true); };
  const aplicar = (texto) => {
    setEditing(false);
    const n = parseMonto(texto);
    if (n == null) return; // vacío o basura → como cancelar
    const r = ajustarPagoInicial({ monto: n, base: precio, minPct, maxPct });
    if (r.monto !== enganche) h.setField({ enganche: r.monto });
    setAviso(r.ajuste ? { texto: mensajeAjuste(r.ajuste, r, { minPct, maxPct }, fmtMXN) } : null);
  };

  const label = (
    <>
      Pago inicial{enganche ? <span className={aviso ? 'eng-bad' : undefined}>{' · ' + fmtMXN(enganche)}</span> : null}
    </>
  );
  return (
    <Field label={label}>
      <div className="eng">
        {editing && (
          <EngancheInput inicial={enganche || ajustarPagoInicial({ monto: 0, base: precio, minPct, maxPct }).monto}
            precio={precio} onApply={aplicar} onCancel={() => setEditing(false)} />
        )}
        <EngancheSlider h={h} minPct={minPct} maxPct={maxPct} />
        {aviso && <div className="eng-msg" role="alert">⚠ {aviso.texto}</div>}
        {puedeEditar && !editing && (
          <button type="button" className="eng-link" onClick={abrir}>Escribir monto</button>
        )}
      </div>
    </Field>
  );
}
function Field({ label, children }) {
  return (<div className="fld"><div className="lab">{label}</div>{children}</div>);
}
const Row = ({ show, children, cls }) => show ? <div className={`rrow ${cls || ''}`}>{children}</div> : null;

/* ── "¿O prefieres…" ──────────────────────────────────────────────────────
 * Pill de Aria + filas compactas (miniatura, nombre+sub, toggle Nueva/Seminueva).
 * El toggle elige la condición; un clic en la fila aplica ese auto/condición y
 * recotiza exacto en la tarjeta de arriba. (Sin precio ni renta aquí.) */
const CarThumb = () => (
  <span className="oa-thumb" aria-hidden>
    <svg width="42" height="21" viewBox="0 0 100 50"><rect x="8" y="22" width="84" height="16" rx="8" fill="#32B8DF" opacity="0.28" /><path d="M28 23 Q33 9 50 9 Q67 9 72 23 Z" fill="#32B8DF" opacity="0.28" /><circle cx="30" cy="39" r="7" fill="#0C2036" opacity="0.32" /><circle cx="70" cy="39" r="7" fill="#0C2036" opacity="0.32" /></svg>
  </span>
);
const Sparkle = () => (<svg width="11" height="11" viewBox="0 0 24 24" fill="#fff" aria-hidden><path d="M12 2 13.8 9.2 21 11 13.8 12.8 12 20 10.2 12.8 3 11 10.2 9.2 Z" /></svg>);

const normCar = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '');

function OtrosAutos({ rows, state, loading, onPick }) {
  // fila SELECCIONADA = la que coincide con el auto que se está cotizando arriba
  // (el sombreado verde sigue a la elección del usuario, no a la rec. de Aria).
  const isSelected = (r) => normCar(state.marca) === normCar(r.marca) && normCar(state.modelo) === normCar(r.modelo);
  // condición visible por fila (toggle local). Default: recomendada = la del
  // auto principal; las demás, Nueva.
  const [cond, setCond] = useState({});
  const condOf = (r) => cond[r.key] || (r.isRec ? (state.condicion === 'seminuevo' ? 'seminuevo' : 'nuevo') : 'nuevo');
  const variantOf = (r) => {
    const want = condOf(r);
    return (want === 'seminuevo' ? r.usada : r.nueva) || r.nueva || r.usada;
  };
  const setRow = (k, c) => setCond((s) => ({ ...s, [k]: c }));

  // pill de Aria: recomienda el auto principal (sin números)
  const rec = rows.find((r) => r.isRec) || rows[0];
  const recCond = rec ? condOf(rec) : 'nuevo';
  const ariaCopy = rec
    ? (recCond === 'seminuevo'
      ? `yo me quedo con ${titleCase(rec.modelo)} seminueva — casi nueva y rinde muchísimo.`
      : `yo me quedo con ${titleCase(rec.modelo)} nueva — impecable y con garantía de agencia.`)
    : '';

  return (
    <div className="oa">
      <div className="oa-head">
        <span className="oa-eyebrow">¿O prefieres explorar otros?</span>
        {ariaCopy && (
          <span className="oa-aria"><span className="oa-aria-av"><Sparkle /></span>
            <span className="oa-aria-tx"><b>Aria:</b> {ariaCopy}</span>
          </span>
        )}
      </div>
      <div className="oa-list">
        {rows.map((r) => {
          const v = variantOf(r);
          const c = condOf(r);
          const hasNueva = !!r.nueva, hasUsada = !!r.usada;
          return (
            <div key={r.key} className={`oa-row${isSelected(r) ? ' sel' : ''}${loading ? ' busy' : ''}`}
              role="button" tabIndex={0}
              onClick={() => !loading && v && onPick({ ...v, condicion: c })}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !loading && v) { e.preventDefault(); onPick({ ...v, condicion: c }); } }}>
              <CarThumb />
              <div className="oa-name">
                <div className="oa-name-t">{titleCase(r.modelo)}{r.isRec && <span className="oa-badge">★ Aria</span>}</div>
                <div className="oa-sub">{titleCase(r.marca)} · {r.sub}</div>
              </div>
              <div className="oa-toggle" onClick={(e) => e.stopPropagation()}>
                {hasNueva && <button className={c === 'nuevo' ? 'on' : ''} onClick={() => setRow(r.key, 'nuevo')}>Nueva</button>}
                {hasUsada && <button className={c === 'seminuevo' ? 'on' : ''} onClick={() => setRow(r.key, 'seminuevo')}>Semi</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Leyendas rotativas mientras cotiza (seguros RODAC + backend tardan unos seg) */
const LOADING_MSGS = [
  'Buscando el mejor seguro para ti…',
  'Comparando aseguradoras…',
  'Armando la mejor cuota…',
  'Afinando tu renta mensual…',
];
function LoadingMsg() {
  const [i, setI] = useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setI(x => (x + 1) % LOADING_MSGS.length), 1700);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="pay placeholder qload" key={i}>
      <span className="qspin" aria-hidden /> {LOADING_MSGS[i]}
    </div>
  );
}

function ResultStrip({ h, onContinue, locked, stale, onRequote }) {
  // Modo manual (Fase 2): renta REAL del backend (h.quote). Modo IA: mock.
  const real = isReal(h);
  const monthly = real ? h.quote.monthly : calcMonthly(h.state);
  const loading = real && h.quote.loading;
  const error = real ? h.quote.error : null;
  // Auto sin seguro cotizable en RODAC ('auto') o sin opciones para el plazo
  // elegido ('plazo') → fallback a asesor por WhatsApp en vez de error crudo.
  const noIns = real && !loading ? h.quote.noInsurance : false;
  const has = monthly > 0 && !loading;
  // Renta lista pero sin lead → DIFUMINADA (curiosidad). Al desbloquear (capturar
  // nombre+WhatsApp) el blur se desvanece, el número cuenta hacia arriba y queda claro.
  const showLocked = has && locked;
  // Ya desbloqueado y el usuario cambió algo → no recotizamos solo: pedimos "Cotizar ahora".
  const showStale = has && !locked && stale;
  // Mientras está bloqueado NO ponemos la renta REAL en el DOM: se muestra un número
  // SEÑUELO (inspeccionar elemento no la revela). La real solo aparece al desbloquear.
  const monthlyAnim = useCountUp(showLocked ? 0 : monthly, 700);

  let btnLabel = 'Continuar';
  if (showLocked) btnLabel = 'Desbloquear';
  else if (showStale) btnLabel = 'Cotizar ahora';

  return (
    <div className="cresult">
      <div>
        <div className="lab">Tu renta mensual</div>
        {loading ? (
          real ? <LoadingMsg /> : <div className="pay placeholder"><span className="qspin" aria-hidden /> Calculando…</div>
        ) : noIns ? (
          <div className="qfall">
            <div className="qfall-t">
              {noIns === 'plazo'
                ? 'Sin seguro disponible para este plazo. Prueba otro plazo o cotiza con un asesor.'
                : 'Este auto requiere cotización con asesor.'}
            </div>
            <button className="qwa" onClick={() => openWhatsApp(h.state)}>
              <WAIcon width="14" height="14" /> Cotizar por WhatsApp
            </button>
          </div>
        ) : error ? (
          <div className="qerr">
            {error} <button className="qretry" onClick={h.retryQuote}>Reintentar</button>
          </div>
        ) : has ? (
          <>
            <div className={`pay${showLocked ? ' pay--locked' : ''}${showStale ? ' pay--stale' : ''}`} aria-hidden={showLocked || undefined}>
              {real && h.quote.compareAt && !showLocked && !showStale ? (
                <span className="was" title="Renta aproximada con otra aseguradora">{fmtMXN(h.quote.compareAt)}</span>
              ) : null}
              {showLocked ? '$0,000' : fmtMXN(monthlyAnim)}<span className="mo">/mes</span>
            </div>
            {showLocked ? (
              <div className="qunlock">Pon tu nombre y WhatsApp para verla clara</div>
            ) : showStale ? (
              <div className="qstale">Cambiaste algo · cotiza de nuevo para actualizar tu renta.</div>
            ) : (
              <>
                <div className="incl"><Ico.Check /> Seguro{real && h.quote.insurer ? ` ${h.quote.insurer}` : ''} · placas · mantenimiento incluidos</div>
                <div className="qdisc">Cotización de ejemplo · la cotización real puede cambiar.</div>
              </>
            )}
          </>
        ) : <div className="pay placeholder">—</div>}
      </div>
      <button className={`cgo${showStale ? ' cgo-requote' : ''}`} disabled={!has} onClick={showStale ? onRequote : onContinue}>
        {btnLabel} <Ico.Arrow />
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Calculadora · STACK (apilada compacta)
   ═══════════════════════════════════════════════════════════════════════ */
export function CalcStack(h) {
  const v = h.visibleRows;
  // % de pago inicial vigente mientras se escribe el precio (ver PriceField abajo)
  const pctRef = useRef(null);
  const done = isReal(h)
    ? h.quote.monthly > 0 && !h.quote.loading
    : calcMonthly(h.state) > 0 && !h.ai.busy;
  return (
    <div className="calc stack">
      <div className="chead">
        <div className="t">{done && <span className="ck"><Ico.Check /></span>}{h.mode === 'manual' ? 'Arma tu plan' : (done ? 'Tu plan · listo' : 'Tu plan')}</div>
        <div className="stat"><span className="d"></span>{done ? 'Listo' : 'Editando'}</div>
      </div>
      <div className="cbody">
        {/* Orden cronológico como /cotizar: Año → Marca → Modelo (RODAC exige año primero) */}
        <Row show={v.has('veh')} cls="g3">
          <Field label="Año">{YearDD(h)}</Field>
          <Field label="Marca">{MarcaDD(h)}</Field>
          <Field label="Modelo">{ModeloDD(h)}</Field>
        </Row>
        <Row show={v.has('ver')} cls="g2-1">
          <Field label="Versión">{VersionDD(h)}</Field>
          <Field label="Precio"><PriceField value={h.state.precio} onChange={(n) => {
            // Conserva el % de enganche al editar el precio (el handoff lo reseteaba
            // a 0 y la renta se quedaba vacía hasta volver a mover el slider).
            // Al peso y dentro de los límites; el % se recuerda entre teclas para
            // que el redondeo de precios a medio escribir no lo vaya moviendo.
            const { precio, enganche } = h.state;
            const prev = pctRef.current;
            const pct = prev && prev.enganche === enganche && prev.precio === precio
              ? prev.pct
              : (precio > 0 && enganche > 0 ? enganche / precio : 0);
            const nuevo = pct && n > 0
              ? ajustarPagoInicial({ monto: n * pct, base: n, ...limitesDe(h) }).monto
              : 0;
            pctRef.current = pct ? { pct, enganche: nuevo, precio: n } : null;
            h.setField({ precio: n, enganche: nuevo });
          }} /></Field>
        </Row>
        <Row show={v.has('tipo')} cls="g3 r-tipo">
          <Field label="Tipo">{CarroceriaDD(h)}</Field>
          <Field label="Motor">{MotorDD(h)}</Field>
          <Field label="Placas">{PlacaDD(h)}</Field>
        </Row>
        <Row show={v.has('plan')}>
          <Field label="Plan">{PlanSeg(h)}</Field>
        </Row>
        <Row show={v.has('term')} cls="g2">
          <Field label={`Plazo${h.state.plazo ? ' · ' + h.state.plazo + ' meses' : ''}`}>{PlazoChips(h)}</Field>
          <EngancheField h={h} />
        </Row>
        {/* Valor residual — solo plan Puro con datos reales; opciones con tope
            por plazo (espejo de ResidualHelper; el backend re-capea igual).
            g2 = media columna (misma escala que Plazo/Pago inicial); el menú
            abre hacia arriba para que no lo recorte el borde del hero. */}
        <Row show={v.has('term') && h.state.plan === 'puro' && isReal(h) && h.residualOptions?.length > 0} cls="g2">
          <Field label={`Valor residual${h.state.residual ? ' · ' + h.state.residual.percentage + '%' : ''}`}>
            <Dropdown
              value={h.state.residual ? h.state.residual.percentage : ''}
              options={h.residualOptions.map(r => ({ value: r.percentage, label: r.percentage + '%' }))}
              placeholder="5% (default)"
              onChange={(p) => h.setResidual(p)}
              compact
              up
            />
          </Field>
          <span aria-hidden />
        </Row>
      </div>
      {v.has('term') && <ResultStrip h={h} onContinue={h.onContinue} locked={h.locked} stale={h.staleQuote} onRequote={h.requote} />}
      {v.has('term') && showSeguroManual(h) && <SeguroManualPanel h={h} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   HeroV3 — opción A (apilada compacta)
   ═══════════════════════════════════════════════════════════════════════ */
export default function HeroV3({ layout = 'stack' }) {
  // F2: useHeroQuote agrega catálogo RODAC + cálculo real (modo manual).
  // F4: suggestRef inyecta el suggester real al pipeline de IA del handoff
  // (backend LLM + resolución RODAC); si falla, useAI cae al mock.
  const suggestRef = useRef(null);
  const h = useHeroQuote(useHeroState(suggestRef), suggestRef);
  const [flow, setFlow] = useState(null); // null | 'lead' | 'modal' | 'cotizador'
  const [leadData, setLeadData] = useState(null); // { nombre, contacto } capturado en LeadModal
  // idle | sending | sent (aceptado, sin confirmar) | delivered | failed
  const [waState, setWaState] = useState('idle');
  // Al que se le mandó de verdad: puede no ser el del lead si reintentó con otro.
  const [waTel, setWaTel] = useState(null);
  const realMonthly = isReal(h) && h.quote.monthly > 0 ? h.quote.monthly : null;

  /* "Continuar por WhatsApp" — el backend le manda la plantilla con su
   * cotización y el lead solo contesta. wa.me NO puede autoenviar (WhatsApp
   * solo prellena la caja; el usuario tiene que darle a enviar), así que
   * invertimos la dirección: escribimos nosotros primero.
   *
   * Si el envío falla —plantilla aún sin aprobar, opt_out, Meta caído— se cae
   * al wa.me de siempre. El lead nunca se queda sin camino. */
  const { enviarWhatsApp, estadoDelWhatsApp } = h;

  /* Manda la plantilla y CONFIRMA si llegó, porque no es lo mismo:
   *   entregado → ya puede chatear con Aria (el hilo existe de su lado)
   *   fallo     → nunca le llegó; hay que ofrecerle otra salida
   *   incierto  → el webhook se tardó; no afirmamos nada
   * `telOverride` es para el reintento con otro número desde el modal. */
  const dispararWhats = async (lead, { conFallback, telOverride = null } = {}) => {
    const params = plantillaParams(h.state, realMonthly);
    const telefono = telOverride || lead?.contacto;
    setWaState('sending');
    const r = await enviarWhatsApp({ telefono, nombre: lead?.nombre, ...params });

    if (r.sent) {
      setWaState('sent');
      setWaTel(telefono);
      const estado = await estadoDelWhatsApp(r.mensaje_id);
      setWaState(estado === 'entregado' ? 'delivered' : estado === 'fallo' ? 'failed' : 'sent');
      return estado;
    }

    setWaState('failed');
    // Solo el camino explícito ("Continuar por WhatsApp") abre wa.me de una: ahí
    // el usuario PIDIÓ el chat y hay que llevarlo. En el envío automático del
    // desbloqueo no le secuestramos la pestaña — venía por su renta, no por un
    // chat; para ese caso el modal le ofrece las salidas cuando lo abra.
    if (conFallback) openWhatsApp(h.state, realMonthly, lead?.nombre);
    return 'fallo';
  };

  const handleWhats = () => dispararWhats(leadData, { conFallback: true });

  // Si ya capturamos el lead, va directo al modal; si no, pasa por LeadModal primero.
  const handleContinue = () => setFlow(leadData ? 'modal' : 'lead');
  const handleLeadSubmit = async (data) => {
    setLeadData(data);
    h.saveLead(data); // best-effort: guarda en el Budget sin bloquear
    // Cierra el modal y deja que la renta difuminada se DESVANEZCA a clara (el
    // momento de payoff). El ContinueModal queda para cuando pulse "Continuar".
    setFlow(null);
    // Casilla marcada (viene así por defecto) → el mensaje sale AQUÍ, en el
    // desbloqueo, sin exigirle un segundo clic en "Continuar por WhatsApp".
    // Sin await: que la renta se revele YA. Si el envío falla, se queda el
    // botón de siempre y él decide.
    if (data?.porWhats) dispararWhats(data, { conFallback: false });
  };

  // "Auto la 1ª vez, luego manual": al desbloquear (lead capturado) entra modo
  // manual; de ahí en adelante los cambios del usuario esperan "Cotizar ahora".
  const { setManualQuote } = h;
  React.useEffect(() => { setManualQuote(!!leadData); }, [leadData, setManualQuote]);

  // Cambiar de modo (IA ↔ Lo hago yo) RE-BLOQUEA: la nueva cotización vuelve a salir
  // con señuelo + LeadModal aunque ya se hubiera desbloqueado en el otro modo.
  React.useEffect(() => { setLeadData(null); setFlow(null); }, [h.mode]);

  /* MSI — mismo gating que la landing vieja (Landing.jsx:17): sesión activa
   * con type_user 1/3/4 y el acceso source-trim habilitado por env. */
  const { authInfo } = useContext(AuthContext);
  const navigate = useNavigate();
  const showMSI = sourceTrimAccessEnabled
    && authInfo?.isAuth
    && ['1', '3', '4'].includes(String(authInfo?.user?.type_user ?? ''));
  h.msi = { show: showMSI, go: () => navigate(sourceTrimRoute) };

  return (
    <div className="heroframe v3 lay-stack">
      <main className="h2-hero">
        <div className="h2-wash" />
        <img className="h2-deco-r" src={rightLines} alt="" aria-hidden />
        <img className="h2-deco-l" src={leftLine} alt="" aria-hidden />
        <div className="h2-slash" />
        <div className="h2-inner">
          <LeftCopy />
          <div className="h2-right">
            <ModeToggle mode={h.mode} onChange={h.switchMode} />

            {h.mode === 'ai' && h.aiPhase === 'input' && (
              <>
                <AICard ai={h.ai} autoFocusOnMount />
                <Sugs prompt={h.ai.prompt} onPick={(p) => { h.ai.setPrompt(p); setTimeout(() => h.ai.run(p), 60); }} />
              </>
            )}

            {h.mode === 'ai' && h.aiPhase === 'thinking' && (
              <>
                <AICard ai={h.ai} small />
                <Thought text={h.ai.thought} busy={h.ai.busy} />
              </>
            )}

            {h.mode === 'ai' && h.aiPhase === 'reveal' && (
              <>
                <PromptStrip prompt={h.ai.prompt} thought={h.ai.thought} busy={h.ai.busy} onEdit={h.restartAI} />
                <CalcStack {...h} onContinue={handleContinue} locked={!leadData} />
                {/* "¿O prefieres…" (diseño V4): pill de Aria + filas con toggle
                    Nueva/Seminueva y renta aproximada. Toggle = solo cambia la
                    renta mostrada; clic en la fila = elige y recotiza exacto. */}
                {h.aiOptions?.length > 0 && !h.ai.busy && (
                  <OtrosAutos rows={h.aiOptions} state={h.state} loading={h.quote.loading} onPick={h.pickVariant} />
                )}
              </>
            )}

            {h.mode === 'manual' && <CalcStack {...h} onContinue={() => { fireQuoteConversion(); handleContinue(); }} locked={!leadData} />}
          </div>
        </div>
      </main>

      <LeadModal
        open={flow === 'lead'}
        onClose={() => setFlow(null)}
        onSubmit={handleLeadSubmit}
      />
      <ContinueModal
        open={flow === 'modal'}
        quote={h.state}
        onClose={() => { setFlow(null); setWaState('idle'); }}
        onWhats={handleWhats}
        onQuote={() => setFlow('cotizador')}
        waState={waState}
        waTelefono={waTel || leadData?.contacto}
        onAbrirWa={() => openWhatsApp(h.state, realMonthly, leadData?.nombre)}
        onOtroNumero={(tel) => dispararWhats(leadData, { conFallback: false, telOverride: tel })}
      />
      {flow === 'cotizador' && (
        <Cotizador
          state={h.state}
          monthly={realMonthly}
          onClose={() => setFlow(null)}
          prefill={leadData}
          /* F3: API real solo en modo manual — seguros RODAC preseleccionados,
             cambio de aseguradora re-cotiza, y envío vía /updateBudget. */
          real={isReal(h) && realMonthly ? {
            options: h.insuranceOptions,
            selectedLogo: h.quote.insurerLogo,
            monthly: h.quote.monthly,
            loading: h.quote.loading,
            setInsurance: h.setInsurance,
            submit: h.submitSolicitud,
          } : null}
        />
      )}
    </div>
  );
}
